// Project save/load — serialize the user's working state to a single .clayforge.json file.
// Triangles ARE the project (sculpt result lives there), so we include them. Texture grids
// are excluded (large + reproducible from the source pattern/SVG).

import type { Triangle } from '../core/types';
import type { ParamGraph } from '../core/paramGraph';
import type { AppState } from './store.svelte';

// v2 adds the parametric node graph. Older v1 files load fine — paramGraph
// defaults to empty when the field is missing.
export const PROJECT_VERSION = 2;
export const PROJECT_EXT = '.clayforge.json';

export interface ProjectFile {
  version: number;
  savedAt: string;
  fileName: string;
  triangles: Triangle[];
  vaseParams: Omit<NonNullable<AppState['vaseParams']>, 'texture'>;
  brush: AppState['brush'];
  settings: AppState['settings'];
  printerId: string;
  textureResolution: number;
  cameraView: AppState['cameraView'];
  lang: AppState['lang'];
  theme: AppState['theme'];
  /** Parametric node graph — added in v2. Optional for v1 backward-compat. */
  paramGraph?: ParamGraph;
}

export function exportProject(app: AppState): Blob {
  const { texture, ...vasePersistable } = app.vaseParams;
  void texture;
  const payload: ProjectFile = {
    version: PROJECT_VERSION,
    savedAt: new Date().toISOString(),
    fileName: app.fileName || 'untitled',
    triangles: app.triangles,
    vaseParams: vasePersistable,
    brush: app.brush,
    settings: app.settings,
    printerId: app.printer.id,
    textureResolution: app.textureResolution,
    cameraView: app.cameraView,
    lang: app.lang,
    theme: app.theme,
    paramGraph: app.paramGraph
  };
  return new Blob([JSON.stringify(payload)], { type: 'application/json' });
}

export function downloadProject(app: AppState): void {
  const blob = exportProject(app);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const base = (app.fileName || 'project').replace(/\.(stl|obj|gcode|json)$/i, '');
  a.href = url;
  a.download = `${base}${PROJECT_EXT}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export async function loadProjectFromFile(file: File): Promise<ProjectFile> {
  const text = await file.text();
  const data = JSON.parse(text) as ProjectFile;
  if (typeof data.version !== 'number' || !Array.isArray(data.triangles)) {
    throw new Error('Geçersiz proje dosyası');
  }
  if (data.version > PROJECT_VERSION) {
    throw new Error(`Bu sürüm v${PROJECT_VERSION}, dosya v${data.version} — daha yeni bir build gerekli`);
  }
  return data;
}
